import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import ExtensionTimer from './ExtensionTimer';
import ExtensionManualEntry from './ExtensionManualEntry';
import ExtensionRecent from './ExtensionRecent';
import ExtensionLogin from './ExtensionLogin';
import { toAbsoluteUrl } from '../../_metronic/helpers';

declare const chrome: any;

export const ExtensionContainer: React.FC = () => {
  const { employeeName, shortName } = useAppSelector((state) => state.globalState);
  

  const [activeTab, setActiveTab] = useState<'track' | 'recent'>('track');
  const [viewMode, setViewMode] = useState<'timer' | 'manual'>('timer');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false);

  const [displayName, setDisplayName] = useState(employeeName || '');
  const [displayInitials, setDisplayInitials] = useState(shortName || '');

  useEffect(() => {
    const checkAuthStatus = () => {
      if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
        chrome.storage.local.get(['accessToken', 'userInfo'], (result: any) => {
          if (result && result.accessToken && result.accessToken !== 'null' && result.accessToken !== 'undefined') {
            setIsAuthenticated(true);
            
            if (result.userInfo) {
              const u = result.userInfo;
              const name = `${u.FirstName || u.first_name || ''} ${u.LastName || u.last_name || ''}`.trim();
              const initials = u.EmployeeInitials || u.shortName || name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
              setDisplayName(name);
              setDisplayInitials(initials);
            }
          } else {
            setIsAuthenticated(false);
          }
          setLoading(false);
        });
      } else {
        const token = localStorage.getItem('accessToken');
        if (token && token !== 'null' && token !== 'undefined') {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
        setLoading(false);
      }
    };

    checkAuthStatus();

    const handleStorageChange = (changes: any, areaName: string) => {
      if (areaName === 'local' && changes.accessToken) {
        if (changes.accessToken.newValue) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.onChanged) {
      chrome.storage.onChanged.addListener(handleStorageChange);
    }

    return () => {
      if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.onChanged) {
        chrome.storage.onChanged.removeListener(handleStorageChange);
      }
    };
  }, []);

  useEffect(() => {
    if (employeeName) setDisplayName(employeeName);
    if (shortName) setDisplayInitials(shortName);
  }, [employeeName, shortName]);

  const handleLogout = () => {
    setShowProfileMenu(false);
    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
      chrome.storage.local.clear();
    }
    localStorage.clear();
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-white">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ExtensionLogin 
        onLoginSuccess={() => setIsAuthenticated(true)} 
      />
    );
  }

  return (
    <div className="d-flex flex-column h-100 bg-white" style={{ minHeight: '100vh', width: '100%' }}>

      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-shrink-0 position-relative">
        <div className="d-flex align-items-center gap-2">
          <img
            alt="Logo"
            src={toAbsoluteUrl('/media/logos/LivetecsLogo.svg')}
            className="h-25px"
          />
        </div>

        <div 
          className="d-flex align-items-center gap-2 cursor-pointer position-relative"
          onClick={() => setShowProfileMenu(!showProfileMenu)}
        >
          <div className="symbol symbol-25px symbol-circle">
            <span className="symbol-label bg-light-primary text-primary fw-bold fs-8">
              {displayInitials || displayName?.trim().split(/\s+/).slice(0, 2).map((name :any) => name.charAt(0).toUpperCase()).join("")}
            </span>
          </div>
          <span className="fw-semibold fs-7 text-gray-800">
            {displayName || 'User'}
          </span>
          <i className={`bi bi-chevron-${showProfileMenu ? 'up' : 'down'} fs-9 text-gray-500`}></i>
        </div>

        {showProfileMenu && (
          <div 
            className="position-absolute end-0 top-100 mt-1 me-4 bg-white rounded shadow-sm border py-2 px-3 z-index-3"
            style={{ minWidth: '140px' }}
          >
            <button
              type="button"
              className="btn btn-active-light-danger w-100 text-start p-2 d-flex align-items-center gap-2 border-0 bg-transparent"
              onClick={handleLogout}
              style={{ color: '#c0392b' }}
            >
              <div 
                className="d-flex align-items-center justify-content-center rounded"
                style={{ backgroundColor: '#fcdbe0', width: '22px', height: '22px' }}
              >
                <i className="bi bi-box-arrow-right fs-8" style={{ color: '#c0392b' }}></i>
              </div>
              <span className="fs-8 fw-semibold" style={{ color: '#c0392b' }}>
                Sign Out
              </span>
            </button>
          </div>
        )}
      </div>

      <div className="px-4 border-bottom flex-shrink-0">
        <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-7 fw-bold w-100">
          <li className="nav-item flex-fill text-center">
            <a
              className={`nav-link text-active-primary py-3 justify-content-center cursor-pointer ${
                activeTab === 'track' ? 'active' : 'text-gray-500'
              }`}
              onClick={() => {
                setActiveTab('track');
                setViewMode('timer');
              }}
            >
              Track Time
            </a>
          </li>
          <li className="nav-item flex-fill text-center">
            <a
              className={`nav-link text-active-primary py-3 justify-content-center cursor-pointer ${
                activeTab === 'recent' ? 'active' : 'text-gray-500'
              }`}
              onClick={() => setActiveTab('recent')}
            >
              Recent
            </a>
          </li>
        </ul>
      </div>

      <div className="p-4 flex-shrink-0">
        {activeTab === 'track' ? (
          viewMode === 'timer' ? (
            <ExtensionTimer onSwitchToManual={() => setViewMode('manual')} />
          ) : (
            <ExtensionManualEntry onBackToTimer={() => setViewMode('timer')} />
          )
        ) : (
          <ExtensionRecent />
        )}
      </div>

      <div className="mt-auto px-4 py-3 d-flex justify-content-between align-items-center bg-white border-top flex-shrink-0">
        <button 
          type="button" 
          className="btn btn-link btn-color-gray-600 btn-active-color-primary p-0 fs-8 fw-semibold text-decoration-none"
          onClick={handleLogout}
        >
          <i className="bi bi-box-arrow-right fs-7 me-1"></i> Sign Out
        </button>
        <button type="button" className="btn btn-link btn-color-gray-600 btn-active-color-primary p-0 fs-8 fw-semibold text-decoration-none">
          <i className="bi bi-question-circle fs-7 me-1"></i> Help
        </button>
      </div>
    </div>
  );
};

export default ExtensionContainer;